import { useTranslation } from 'react-i18next';
import type { Activity } from '../../../types';
import { StopTime, stopLabel } from './MealStopLink';
import { Route, Icon } from '../../ui/Icon';

/** What a new meal should be in the itinerary, decided before it is saved. */
export type StopChoice =
  | { kind: 'none' }
  | { kind: 'link'; activityId: string }
  | { kind: 'create'; startTime: string };

interface Props {
  linkable: Activity[];
  value: StopChoice;
  onChange: (choice: StopChoice) => void;
}

/**
 * The add form's side of ADR-0024: the same two doors as editing a meal —
 * link a stop of the day, or make one — but only chosen here; they are applied
 * once the meal exists.
 */
export default function MealStopChoice({ linkable, value, onChange }: Props) {
  const { t } = useTranslation();
  const creating = value.kind === 'create';

  return (
    <div className="space-y-1">
      <div className="flex flex-col sm:flex-row gap-1.5">
        {linkable.length > 0 && (
          <select
            value={value.kind === 'link' ? value.activityId : ''}
            onChange={e => onChange(e.target.value ? { kind: 'link', activityId: e.target.value } : { kind: 'none' })}
            aria-label={t('meals.stop.pick')}
            className="input sm:!w-44 shrink-0 !text-xs !px-2 !py-1.5 cursor-pointer"
          >
            <option value="">{t('meals.stop.pick')}</option>
            {linkable.map(a => <option key={a.id} value={a.id}>{stopLabel(a)}</option>)}
          </select>
        )}
        <div className="flex items-stretch gap-1.5">
          <button
            type="button"
            onClick={() => onChange(creating ? { kind: 'none' } : { kind: 'create', startTime: '' })}
            aria-pressed={creating}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
              creating
                ? 'bg-teal-600 text-white hover:bg-teal-700'
                : 'text-teal-700 bg-teal-50 hover:bg-teal-100 dark:text-teal-300 dark:bg-teal-900/25 dark:hover:bg-teal-900/40'
            }`}
          >
            <Icon as={Route} size={12} />
            {t('meals.stop.create')}
          </button>
          {creating && (
            <StopTime value={value.startTime} onChange={startTime => onChange({ kind: 'create', startTime })} />
          )}
        </div>
      </div>
      {value.kind !== 'none' && <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('meals.stop.hint')}</p>}
    </div>
  );
}
